import { ChangeEvent, useEffect, useState } from 'react'
import { Grid, useMediaQuery, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { Filter, PaginationButtons, PaginationContext } from 'src/components'
import { TranslationsTable } from './translations-table'
import { TranslationsGrid } from './translations-grid'
import { Counter } from './counter'
import { NavButtons } from './nav-buttons'
import {
  initialTranslationsFilter,
  useFilter,
  useLanguagesSelectionOption,
  useMe,
  usePagination,
  useTableItemsCheck,
  useTranslations,
} from 'src/hooks'

import { countOffset, sortToObject } from 'src/common/utils'
import {
  defaultPage,
  defaultSort,
  translationsSortOptions,
  defaultTranslationsLimit,
  translationsChecboxOptions,
} from './utils'
import { DEFAULT_LANGUAGE_CODE } from './constants'

const TranslationsList = () => {
  const [t] = useTranslation()
  const { data: me } = useMe()
  const [filter, setFilter] = useFilter({ initialState: initialTranslationsFilter })
  const [sort, setSort] = useState(defaultSort)
  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination(
    defaultPage,
    defaultTranslationsLimit,
  )
  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  const { data: translations, refetch: refetchTranslations } = useTranslations({
    ...filter,
    offset: countOffset(page, rowsPerPage),
    limit: rowsPerPage,
    ...sortToObject(sort),
  })

  const {
    handleItemCheck,
    isItemSelected,
    selectedItemsIds,
    setSelectedItemsIds,
    isIndeterminated,
    isAllChecked,
    isAllUnchecked,
  } = useTableItemsCheck({
    items: translations?.data ?? [],
  })

  const handleAllItemsCheck = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && selectedItemsIds.length === 0 && translations?.data) {
      return setSelectedItemsIds(
        translations.data
          .filter((item) => item.language.code !== DEFAULT_LANGUAGE_CODE)
          .map(({ id }) => id),
      )
    }

    setSelectedItemsIds([])
  }

  useEffect(() => {
    refetchTranslations()
  }, [filter, sort, page, rowsPerPage, refetchTranslations])

  useEffect(() => {
    setPage(defaultPage)
  }, [filter, setPage, sort])

  const languagesSelectionOptions = useLanguagesSelectionOption()

  const paginationValues = {
    count: translations?.count ?? 0,
    page: page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    isShown: !!translations && (translations.count >= rowsPerPage || Number.isNaN(rowsPerPage)),
    withAll: true,
  }

  const languageCode = me ? me.language.code : DEFAULT_LANGUAGE_CODE

  return (
    <Grid container>
      <Filter
        noClear={true}
        sort={sort}
        setSort={setSort}
        searchFieldText={t('translations-search-input.v')}
        filter={filter}
        setFilter={setFilter}
        selectionOptions={[{ ...languagesSelectionOptions, withSearch: true }]}
        sortOptions={translationsSortOptions}
        checkboxOptions={translationsChecboxOptions}
        initialState={initialTranslationsFilter}
      />
      <div style={{ flexGrow: 1 }}>
        <Counter
          setFilter={setFilter}
          filter={filter}
          missing={translations?.missingTranslationsCount}
          total={translations?.total}
          required={translations?.reviewRequiredCount}
        />
        <NavButtons
          languageCode={filter.locale as string}
          translationDisabled={
            (filter['locale'] === DEFAULT_LANGUAGE_CODE ||
              translations?.missingTranslationsCount === 0) &&
            isAllUnchecked
          }
          selectedItemsIds={selectedItemsIds}
          setSelectedItemsIds={setSelectedItemsIds}
        />
      </div>
      <PaginationContext.Provider value={paginationValues}>
        {isTablet ? (
          <TranslationsGrid languageCode={languageCode} items={translations?.data ?? []} />
        ) : (
          <TranslationsTable
            languageCode={languageCode}
            items={translations?.data}
            setSort={setSort}
            sort={sort}
            isIndeterminated={isIndeterminated}
            isAllChecked={isAllChecked}
            isItemSelected={isItemSelected}
            handleItemCheck={handleItemCheck}
            handleAllItemsCheck={handleAllItemsCheck}
          />
        )}
        {!isTablet && <PaginationButtons />}
      </PaginationContext.Provider>
    </Grid>
  )
}

export { TranslationsList }
